/**
 * Tema do website institucional (claro ou escuro)
 *
 * O padrão é o tema claro; o escuro fica disponível no botão do cabeçalho. A escolha do
 * visitante fica salva no navegador e é aplicada como `data-site-theme` no <html>
 * (o CSS do site troca as cores com base nesse atributo, ver website.css).
 * Independente do tema do painel administrativo.
 *
 * @author Equipe VendeAi
 * @since 1.3.0
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SiteTheme = 'dark' | 'light';

export const SITE_THEME_STORAGE_KEY = 'vendeai-site-theme';

/** Aplica o tema no documento (chamado também antes da primeira renderização, em main.tsx) */
export function applySiteTheme(theme: SiteTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-site-theme', theme);
}

/** Lê o tema salvo sem depender do React (evita piscar o tema errado ao abrir o site) */
export function readStoredSiteTheme(): SiteTheme {
  try {
    const raw = localStorage.getItem(SITE_THEME_STORAGE_KEY);
    const theme = raw
      ? (JSON.parse(raw) as { state?: { theme?: string } }).state?.theme
      : undefined;
    return theme === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

interface SiteThemeState {
  theme: SiteTheme;
  toggleTheme: () => void;
}

export const useSiteTheme = create<SiteThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      toggleTheme: () => {
        const theme: SiteTheme = get().theme === 'dark' ? 'light' : 'dark';
        applySiteTheme(theme);
        set({ theme });
      },
    }),
    {
      name: SITE_THEME_STORAGE_KEY,
      onRehydrateStorage: () => state => {
        if (state) applySiteTheme(state.theme);
      },
    },
  ),
);
